/*
 * ====================================================================================
 * FILE: ChangeStatusDialog.jsx
 * PURPOSE: Super-admin dialog to move a machine through its lifecycle statuses:
 *          On Rent → De-hired → On Service / Breakdown → Ready for Rent →
 *          Waiting for Rent → On Rent.
 *
 * Two steps:
 *   1. Pick the NEW status + capture closing details of the CURRENT status
 *      (e.g. leaving "On Rent" asks when the rental started/ended and which
 *      contract; leaving "On Service" asks what work was done — logged to
 *      service_logs and the ticket is closed).
 *   2. Capture details of the NEW status (e.g. entering "Waiting for Rent"
 *      asks for the confirmed contract + gate entry / safety compliance).
 *
 * Backend: POST /api/tracking/device/:id/status  { toStatus, closing, opening }
 * ====================================================================================
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, FormControl, InputLabel, Select,
  Stepper, Step, StepLabel, Box, Typography, Chip, Stack,
  FormControlLabel, Checkbox, Autocomplete, Alert
} from '@mui/material';
import mewpService from '../../services/mewpService';
import { MACHINE_STATUSES, statusLabel, statusColor } from '../../utils/statusLabels';
import { machineLabel } from '../../utils/machineLabel';

const todayStr = () => new Date().toISOString().slice(0, 10);

const normalize = (s) => (s || 'UNKNOWN').toUpperCase().replace(/\s+/g, '_');

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };

const ChangeStatusDialog = ({ open, onClose, device, currentStatus, onSuccess }) => {
  const fromStatus = normalize(currentStatus);

  const [step, setStep] = useState(0);
  const [toStatus, setToStatus] = useState('');
  const [closing, setClosing] = useState({});
  const [opening, setOpening] = useState({});
  const [contracts, setContracts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form every time the dialog opens (or for a different machine)
  useEffect(() => {
    if (open) {
      setStep(0);
      setToStatus('');
      setClosing(fromStatus === 'RENTAL' ? { rentalEndDate: todayStr() } : {});
      setOpening({});
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.equipment_id]);

  // Contract list is needed when closing a rental or entering RENTAL / WAITING_FOR_RENT
  const needContracts = open && (fromStatus === 'RENTAL' || toStatus === 'RENTAL' || toStatus === 'WAITING_FOR_RENT');
  useEffect(() => {
    if (needContracts && contracts.length === 0) {
      mewpService.getContracts()
        .then(res => setContracts(res.data || res || []))
        .catch(() => { /* picker degrades to free text */ });
    }
  }, [needContracts, contracts.length]);

  const setClosingField = (name, value) => setClosing(prev => ({ ...prev, [name]: value }));
  const setOpeningField = (name, value) => setOpening(prev => ({ ...prev, [name]: value }));

  const contractOption = (id) => contracts.find(c => c.rental_contract_id === id) || null;
  const contractLabel = (c) => c ? `${c.rental_contract_id} • ${c.company_name || 'Unknown customer'}${c.site_location ? ` • ${c.site_location}` : ''}` : '';

  // Going On Rent → Service/Breakdown keeps the rental contract ACTIVE
  // (machine breaks down / gets serviced at the customer site) — only a
  // service ticket is asked for, no rental closure.
  const serviceTarget = toStatus === 'SERVICE' || toStatus === 'BREAKDOWN';

  // ---- Validation per step ----
  const step1Valid = () => {
    if (!toStatus) return false;
    if (fromStatus === 'SERVICE' || fromStatus === 'BREAKDOWN' || fromStatus === 'MAINTENANCE') return !!closing.actionTaken;
    if (fromStatus === 'RENTAL') return serviceTarget || !!closing.rentalEndDate;
    return true;
  };
  const step2Valid = () => {
    if (toStatus === 'RENTAL' || toStatus === 'WAITING_FOR_RENT') return !!opening.rentalContractId;
    if (toStatus === 'BREAKDOWN') return !!opening.description;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await mewpService.changeMachineStatus(device.equipment_id || device.device_id, { toStatus, closing, opening });
      onSuccess?.(toStatus);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to change status');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 1: closing details of the CURRENT status ----
  const renderClosingFields = () => {
    if (fromStatus === 'RENTAL') {
      // On Rent → Service/Breakdown: the rental is NOT ending — skip contract
      // closure, the next step asks for the service ticket details.
      if (serviceTarget) {
        return (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              The rental contract stays active — the machine is going on {statusLabel(toStatus).toLowerCase()} during the rental. A service ticket will be opened in the next step.
            </Alert>
            <TextField label="Remarks (optional)" multiline minRows={2} value={closing.note || ''} onChange={e => setClosingField('note', e.target.value)} sx={fieldSx} />
          </Stack>
        );
      }
      return (
        <Stack spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            This machine is On Rent — record how the rental ended before changing status.
          </Typography>
          <Autocomplete
            options={contracts}
            value={contractOption(closing.rentalContractId)}
            onChange={(_, c) => setClosingField('rentalContractId', c?.rental_contract_id || '')}
            getOptionLabel={contractLabel}
            isOptionEqualToValue={(o, v) => o.rental_contract_id === v.rental_contract_id}
            renderInput={(params) => (
              <TextField {...params} label="Rental contract" helperText="Leave empty to close the machine's active contract automatically" sx={fieldSx} />
            )}
          />
          <TextField label="Rental started on" type="date" value={closing.rentalStartDate || ''} onChange={e => setClosingField('rentalStartDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField label="Rental ended / de-hired on" type="date" required value={closing.rentalEndDate || ''} onChange={e => setClosingField('rentalEndDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField label="Remarks" multiline minRows={2} value={closing.note || ''} onChange={e => setClosingField('note', e.target.value)} sx={fieldSx} />
        </Stack>
      );
    }
    if (fromStatus === 'SERVICE' || fromStatus === 'BREAKDOWN' || fromStatus === 'MAINTENANCE') {
      return (
        <Stack spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            Record the service work done — this is logged to the service ticket and the ticket is closed.
          </Typography>
          <TextField label="Action taken / work done" required multiline minRows={2} value={closing.actionTaken || ''} onChange={e => setClosingField('actionTaken', e.target.value)} sx={fieldSx} />
          <TextField label="Remarks" multiline minRows={2} value={closing.remarks || ''} onChange={e => setClosingField('remarks', e.target.value)} sx={fieldSx} />
        </Stack>
      );
    }
    if (fromStatus === 'WAITING_FOR_RENT') {
      return (
        <Stack spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            Closing the waiting period — confirm gate entry and safety compliance.
          </Typography>
          <TextField label="Gate entry date" type="date" value={closing.gateEntryDate || ''} onChange={e => setClosingField('gateEntryDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <FormControlLabel
            control={<Checkbox checked={!!closing.safetyComplianceConfirmed} onChange={e => setClosingField('safetyComplianceConfirmed', e.target.checked)} />}
            label="Customer safety standards met"
          />
          <TextField label="Remarks" multiline minRows={2} value={closing.note || ''} onChange={e => setClosingField('note', e.target.value)} sx={fieldSx} />
        </Stack>
      );
    }
    // DE_HIRED / AVAILABLE / UNKNOWN — only an optional note
    return (
      <TextField fullWidth label={`Notes on leaving "${statusLabel(fromStatus)}" (optional)`} multiline minRows={2} value={closing.note || ''} onChange={e => setClosingField('note', e.target.value)} sx={fieldSx} />
    );
  };

  // ---- Step 2: details of the NEW status ----
  const renderOpeningFields = () => {
    switch (toStatus) {
      case 'RENTAL':
        return (
          <Stack spacing={2}>
            <Autocomplete
              options={contracts}
              value={contractOption(opening.rentalContractId)}
              onChange={(_, c) => setOpeningField('rentalContractId', c?.rental_contract_id || '')}
              getOptionLabel={contractLabel}
              isOptionEqualToValue={(o, v) => o.rental_contract_id === v.rental_contract_id}
              renderInput={(params) => <TextField {...params} required label="Rental contract" sx={fieldSx} />}
            />
            <TextField label="Rental starts on" type="date" value={opening.rentalStartDate || ''} onChange={e => setOpeningField('rentalStartDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <TextField label="Remarks" multiline minRows={2} value={opening.note || ''} onChange={e => setOpeningField('note', e.target.value)} sx={fieldSx} />
          </Stack>
        );
      case 'SERVICE':
      case 'BREAKDOWN':
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              A service ticket will be opened for this machine.
            </Typography>
            {toStatus === 'SERVICE' && (
              <FormControl fullWidth>
                <InputLabel>Service type</InputLabel>
                <Select label="Service type" value={opening.issueType || 'PREVENTIVE_MAINTENANCE'} onChange={e => setOpeningField('issueType', e.target.value)} sx={{ borderRadius: '12px' }}>
                  <MenuItem value="PREVENTIVE_MAINTENANCE">Preventive Maintenance</MenuItem>
                  <MenuItem value="INSPECTION">Inspection</MenuItem>
                  <MenuItem value="INSTALLATION">Installation</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select label="Priority" value={opening.priority || (toStatus === 'BREAKDOWN' ? 'HIGH' : 'MEDIUM')} onChange={e => setOpeningField('priority', e.target.value)} sx={{ borderRadius: '12px' }}>
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
              </Select>
            </FormControl>
            <TextField label={toStatus === 'BREAKDOWN' ? 'Breakdown description' : 'Issue / work description'} required={toStatus === 'BREAKDOWN'} multiline minRows={2} value={opening.description || ''} onChange={e => setOpeningField('description', e.target.value)} sx={fieldSx} />
            <TextField label="Machine location" value={opening.location || ''} onChange={e => setOpeningField('location', e.target.value)} sx={fieldSx} />
          </Stack>
        );
      case 'DE_HIRED':
        return (
          <Stack spacing={2}>
            <TextField label="De-hired on" type="date" value={opening.dehireDate || todayStr()} onChange={e => setOpeningField('dehireDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <TextField label="Reason / condition notes" multiline minRows={2} value={opening.dehireReason || ''} onChange={e => setOpeningField('dehireReason', e.target.value)} sx={fieldSx} />
            <TextField label="Current machine location" value={opening.location || ''} onChange={e => setOpeningField('location', e.target.value)} sx={fieldSx} />
          </Stack>
        );
      case 'AVAILABLE':
        return (
          <Stack spacing={2}>
            <FormControlLabel
              control={<Checkbox checked={!!opening.inspectionPassed} onChange={e => setOpeningField('inspectionPassed', e.target.checked)} />}
              label="Inspection passed — machine is ready for rent"
            />
            <TextField label="Remarks" multiline minRows={2} value={opening.note || ''} onChange={e => setOpeningField('note', e.target.value)} sx={fieldSx} />
          </Stack>
        );
      case 'WAITING_FOR_RENT':
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Machine confirmed for a rental contract — waiting for gate entry and customer safety clearance.
            </Typography>
            <Autocomplete
              options={contracts}
              value={contractOption(opening.rentalContractId)}
              onChange={(_, c) => setOpeningField('rentalContractId', c?.rental_contract_id || '')}
              getOptionLabel={contractLabel}
              isOptionEqualToValue={(o, v) => o.rental_contract_id === v.rental_contract_id}
              renderInput={(params) => <TextField {...params} required label="Confirmed rental contract" sx={fieldSx} />}
            />
            <TextField label="Expected gate entry date" type="date" value={opening.expectedGateEntryDate || ''} onChange={e => setOpeningField('expectedGateEntryDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <FormControlLabel
              control={<Checkbox checked={!!opening.safetyComplianceConfirmed} onChange={e => setOpeningField('safetyComplianceConfirmed', e.target.checked)} />}
              label="Customer safety standards already verified"
            />
            <TextField label="Remarks" multiline minRows={2} value={opening.note || ''} onChange={e => setOpeningField('note', e.target.value)} sx={fieldSx} />
          </Stack>
        );
      case 'TESTING':
      case 'SALE':
        return (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              Internal status — this only tags the machine as {statusLabel(toStatus).toLowerCase()}. No service ticket or rental contract is created, and it stays out of the rental fleet and service queues.
            </Alert>
            <TextField label="Current machine location" value={opening.location || ''} onChange={e => setOpeningField('location', e.target.value)} sx={fieldSx} />
            <TextField label="Remarks" multiline minRows={2} value={opening.note || ''} onChange={e => setOpeningField('note', e.target.value)} sx={fieldSx} />
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        Change Machine Status
        <Typography variant="body2" color="text.secondary">
          {machineLabel(device)} {device?.model_number ? `• ${device.model_number}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          <Step><StepLabel>Close current status</StepLabel></Step>
          <Step><StepLabel>New status details</StepLabel></Step>
        </Stepper>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip label={statusLabel(fromStatus)} color={statusColor(fromStatus)} size="small" sx={{ fontWeight: 800 }} />
          <Typography variant="body2" color="text.secondary">→</Typography>
          {toStatus
            ? <Chip label={statusLabel(toStatus)} color={statusColor(toStatus)} size="small" sx={{ fontWeight: 800 }} />
            : <Typography variant="body2" color="text.secondary">select new status</Typography>}
        </Box>

        {step === 0 && (
          <Stack spacing={2.5}>
            <FormControl fullWidth required>
              <InputLabel>New status</InputLabel>
              <Select label="New status" value={toStatus} onChange={e => setToStatus(e.target.value)} sx={{ borderRadius: '12px' }}>
                {MACHINE_STATUSES.filter(s => s !== fromStatus).map(s => (
                  <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {renderClosingFields()}
          </Stack>
        )}

        {step === 1 && renderOpeningFields()}

        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px' }}>Cancel</Button>
        {step === 1 && (
          <Button onClick={() => setStep(0)} disabled={submitting} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px' }}>Back</Button>
        )}
        {step === 0 ? (
          <Button variant="contained" disabled={!step1Valid()} onClick={() => setStep(1)} sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '10px', px: 4 }}>
            Next
          </Button>
        ) : (
          <Button variant="contained" disabled={submitting || !step2Valid()} onClick={handleSubmit} sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '10px', px: 4 }}>
            {submitting ? 'Saving…' : 'Change Status'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChangeStatusDialog;
